import React, { useState, Suspense, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Slider,
  Button,
  Grid,
  Chip,
} from '@mui/material'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { motion } from 'framer-motion'
import BlackHoleScene from '../components/BlackHoleScene'

const SimulationPage = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [gravityEnabled, setGravityEnabled] = useState(true)
  const [cameraDistance, setCameraDistance] = useState(15)
  const [diskBrightness, setDiskBrightness] = useState(1.0)
  const [lensingStrength, setLensingStrength] = useState(1.5)
  const [fps, setFps] = useState(60)
  const [resolution, setResolution] = useState('1920x1080')
  const [showAdvancedControls, setShowAdvancedControls] = useState(false)
  
  // Advanced physics parameters
  const [blackHoleMass, setBlackHoleMass] = useState(4.3e6) // Sagittarius A* default
  const [accretionDiskDensity, setAccretionDiskDensity] = useState(1000)
  const [cameraFOV, setCameraFOV] = useState(75)
  const [blackHoleSpin, setBlackHoleSpin] = useState(0.9)
  const [particleCount, setParticleCount] = useState(1000)

  // Handle parameters from ExplorePage via URL
  useEffect(() => {
    console.log('SimulationPage useEffect triggered')
    console.log('Current searchParams:', searchParams.toString())
    
    const mass = searchParams.get('mass')
    const spin = searchParams.get('spin')
    const distance = searchParams.get('distance')
    const name = searchParams.get('name')
    
    console.log('URL params:', { mass, spin, distance, name })
    
    if (mass) {
      const massValue = parseFloat(mass)
      setBlackHoleMass(massValue)
      console.log('Set black hole mass to:', massValue)
    }
    if (spin) {
      const spinValue = parseFloat(spin)
      setBlackHoleSpin(spinValue)
      console.log('Set black hole spin to:', spinValue)
    }
    if (distance) {
      const distanceValue = parseFloat(distance)
      const scaledDistance = Math.min(distanceValue / 1000000, 100)
      setCameraDistance(scaledDistance)
      console.log('Set camera distance to:', scaledDistance)
    }
  }, [searchParams])

  const handleFPSUpdate = (newFps) => {
    setFps(Math.round(newFps))
  }

  const resetCamera = () => {
    setCameraDistance(15)
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
      {/* Control Panel */}
      <Paper
        sx={{
          width: 350,
          p: 3,
          borderRadius: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRight: '1px solid #333',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
          🌌 Black Hole Controls
          {searchParams.get('name') && (
            <Chip
              label={`Simulating: ${searchParams.get('name')}`}
              size="small"
              sx={{ 
                ml: 2, 
                backgroundColor: 'secondary.main', 
                color: 'white',
                fontFamily: 'Roboto, sans-serif'
              }}
            />
          )}
        </Typography>

        {/* Gravity Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={gravityEnabled}
                onChange={(e) => setGravityEnabled(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                Enable Gravity
              </Typography>
            }
          />
        </Box>

        {/* Camera Distance */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom sx={{ color: 'text.secondary' }}>
            Camera Distance
          </Typography>
          <Slider
            value={cameraDistance}
            onChange={(e, value) => setCameraDistance(value)}
            min={5}
            max={50}
            step={1}
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                backgroundColor: 'primary.main',
              },
            }}
          />
        </Box>

        {/* Disk Brightness */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom sx={{ color: 'text.secondary' }}>
            Accretion Disk Brightness
          </Typography>
          <Slider
            value={diskBrightness}
            onChange={(e, value) => setDiskBrightness(value)}
            min={0}
            max={2}
            step={0.1}
            sx={{
              color: 'secondary.main',
              '& .MuiSlider-thumb': {
                backgroundColor: 'secondary.main',
              },
            }}
          />
        </Box>

        {/* Lensing Strength */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom sx={{ color: 'text.secondary' }}>
            Gravitational Lensing
          </Typography>
          <Slider
            value={lensingStrength}
            onChange={(e, value) => setLensingStrength(value)}
            min={0}
            max={3}
            step={0.1}
            sx={{
              color: 'success.main',
              '& .MuiSlider-thumb': {
                backgroundColor: 'success.main',
              },
            }}
          />
        </Box>

        {/* Reset Button */}
        <Button
          variant="contained"
          onClick={resetCamera}
          sx={{
            width: '100%',
            mb: 3,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Reset Camera
        </Button>

        {/* Advanced Controls Toggle */}
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowAdvancedControls(!showAdvancedControls)}
          sx={{
            mb: 2,
            fontFamily: 'Orbitron, sans-serif',
            borderColor: 'secondary.main',
            '&:hover': {
              borderColor: 'secondary.light',
              backgroundColor: 'rgba(156, 39, 176, 0.1)',
            },
          }}
        >
          🔬 {showAdvancedControls ? 'Hide' : 'Show'} Advanced Controls
        </Button>

        {/* Advanced Physics Controls */}
        {showAdvancedControls && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                border: '1px solid #9c27b0',
              }}
            >
              <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2, fontFamily: 'Orbitron, sans-serif' }}>
                🔬 Advanced Physics
              </Typography>
              
              <Typography gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
                Black Hole Mass: {(blackHoleMass / 1e6).toFixed(1)}M☉
              </Typography>
              <Slider
                value={Math.log10(blackHoleMass)}
                onChange={(e, newValue) => setBlackHoleMass(Math.pow(10, newValue))}
                min={0} // 1 solar mass
                max={10} // 10 billion solar masses
                step={0.1}
                color="secondary"
                sx={{ mb: 2 }}
              />

              <Typography gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
                Black Hole Spin: {blackHoleSpin.toFixed(2)}
              </Typography>
              <Slider
                value={blackHoleSpin}
                onChange={(e, newValue) => setBlackHoleSpin(newValue)}
                min={0}
                max={0.998}
                step={0.01}
                color="secondary"
                sx={{ mb: 2 }}
              />

              <Typography gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
                Accretion Disk Density: {accretionDiskDensity}
              </Typography>
              <Slider
                value={accretionDiskDensity}
                onChange={(e, newValue) => setAccretionDiskDensity(newValue)}
                min={100}
                max={5000}
                step={100}
                color="secondary"
                sx={{ mb: 2 }}
              />

              <Typography gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
                Particle Count: {particleCount}
              </Typography>
              <Slider
                value={particleCount}
                onChange={(e, newValue) => setParticleCount(newValue)}
                min={100}
                max={5000}
                step={100}
                color="secondary"
                sx={{ mb: 2 }}
              />

              <Typography gutterBottom sx={{ fontFamily: 'Roboto, sans-serif' }}>
                Camera FOV: {cameraFOV}°
              </Typography>
              <Slider
                value={cameraFOV}
                onChange={(e, newValue) => setCameraFOV(newValue)}
                min={30}
                max={120}
                step={5}
                color="secondary"
              />
            </Paper>
          </motion.div>
        )}

        {/* Performance Info */}
        <Paper
          sx={{
            p: 2,
            backgroundColor: 'rgba(0, 255, 0, 0.1)',
            border: '1px solid #00ff00',
          }}
        >
          <Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>
            📊 Performance
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Chip
                label={`FPS: ${fps}`}
                size="small"
                sx={{ backgroundColor: 'success.main', color: 'black' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Chip
                label={`Res: ${resolution}`}
                size="small"
                sx={{ backgroundColor: 'primary.main', color: 'white' }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Educational Info */}
        <Paper
          sx={{
            p: 2,
            mt: 2,
            backgroundColor: 'rgba(0, 255, 255, 0.1)',
            border: '1px solid #00ffff',
          }}
        >
          <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
            🔬 Scientific Features
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            • Schwarzschild geodesics
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            • Gravitational lensing effects
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            • Real-time physics simulation
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            • Event horizon visualization
          </Typography>
        </Paper>
      </Paper>

      {/* 3D Canvas */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <Canvas
          camera={{
            position: [0, 0, cameraDistance],
            fov: cameraFOV,
          }}
          style={{ 
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)'
          }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance"
          }}
          onCreated={({ gl, camera }) => {
            // Enable anti-aliasing and high quality rendering
            gl.setPixelRatio(Math.min(window.devicePixelRatio, 2))
            gl.antialias = true
            gl.outputColorSpace = 'srgb'
            
            // Handle WebGL context lost/restored
            gl.domElement.addEventListener('webglcontextlost', (event) => {
              event.preventDefault()
              console.warn('WebGL context lost, attempting to restore...')
            })
            gl.domElement.addEventListener('webglcontextrestored', () => {
              console.log('WebGL context restored')
            })
          }}
        >
              <Suspense fallback={null}>
            <OrbitControls
              enableDamping
              dampingFactor={0.05}
              minDistance={2}
              maxDistance={100}
              autoRotate={false}
            />
            <BlackHoleScene
              gravityEnabled={gravityEnabled}
              onFPSUpdate={handleFPSUpdate}
              blackHoleMass={blackHoleMass}
              particleCount={particleCount}
              diskDensity={accretionDiskDensity}
              blackHoleSpin={blackHoleSpin}
              diskBrightness={diskBrightness}
              lensingStrength={lensingStrength}
            />
          </Suspense>
        </Canvas>

      </Box>
    </Box>
  )
}

export default SimulationPage
